import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AutomationWire } from '~api';
import { api } from '~portal/api';
import type { ScheduleEditorValue } from '~portal/components/admin/automations/ScheduleEditor';
import { SchedulesView } from '~portal/components/admin/automations/SchedulesView';
import { logger } from '~portal/lib';
import { errorMessage, isConflict } from '~portal/lib/automations';

/**
 * @desc The Schedules page (H2-7, task 8.6); the page itself is `SchedulesView`. Writes refetch
 * the list on success, and every other admin's page refetches on the room's `schedule` hint.
 */
export const Route = createFileRoute('/_protected/admin/ario/schedules')({
  component: SchedulesPage,
});

function SchedulesPage() {
  const utils = api.useUtils();
  const list = api.automations.schedule.list.useQuery({});
  const workflows = api.automations.workflow.list.useQuery({ limit: 200 });
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const create = api.automations.schedule.create.useMutation();
  const update = api.automations.schedule.update.useMutation();
  const setEnabled = api.automations.schedule.setEnabled.useMutation();
  const archive = api.automations.schedule.archive.useMutation();

  /** @desc One write; resolves `true` once accepted and the list has refetched. */
  const write = async (action: string, call: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    setConflict(false);
    try {
      await call();
      await utils.automations.schedule.list.invalidate();
      return true;
    } catch (err) {
      if (isConflict(err)) {
        setConflict(true);
      } else {
        setError(errorMessage(err));
        logger.warn('[Schedules] Write refused', { action, error: errorMessage(err) });
      }
      return false;
    }
  };

  const token = (schedule: AutomationWire.Schedule) => ({
    id: schedule._id,
    updatedAt: schedule.updatedAt,
  });

  return (
    <SchedulesView
      schedules={list.data ?? []}
      workflows={(workflows.data ?? []).map((workflow) => ({
        id: workflow._id,
        name: workflow.name,
      }))}
      loading={list.isLoading}
      error={error ?? list.error?.message ?? null}
      conflict={conflict}
      onRefresh={() => {
        setConflict(false);
        setError(null);
        void utils.automations.schedule.list.invalidate();
      }}
      onSetEnabled={(schedule, enabled, reason) =>
        write(enabled ? 'enable' : 'disable', () =>
          setEnabled.mutateAsync({ ...token(schedule), enabled, ...(reason ? { reason } : {}) }),
        )
      }
      onArchive={(schedule, reason) =>
        write('archive', () => archive.mutateAsync({ ...token(schedule), reason }))
      }
      onCreate={(value: ScheduleEditorValue) =>
        write('create', () =>
          create.mutateAsync({
            workflowId: value.workflowId ?? '',
            cron: value.cron,
            misfirePolicy: value.misfirePolicy,
            paramOverrides: {},
            // D23: a schedule starts disabled; enabling is its own confirmed act.
            enabled: false,
          }),
        )
      }
      onUpdate={(schedule, value) =>
        write('update', () =>
          update.mutateAsync({
            ...token(schedule),
            cron: value.cron,
            misfirePolicy: value.misfirePolicy,
          }),
        )
      }
    />
  );
}
